import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ArrowRight, Sparkles, Shield, Globe, Menu, X, TrendingUp, CreditCard, AlertCircle } from 'lucide-react';
import { QykfinLogoSimple } from './QykfinLogo';
import LanguageToggle from './LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { trackClick } from '../services/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const HeroSection = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t, isRTL } = useLanguage();

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    // Track click
    trackClick('hero-submit-btn', 'Join Waitlist', 'Landing Page');
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/waitlist`, {
        name: email.split('@')[0],
        email,
        interest_type: 'user',
        utm_source: new URLSearchParams(window.location.search).get('utm_source') || 'direct'
      });
      toast.success('Welcome to Qykfin! We\'ll be in touch soon.');
      setEmail('');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <section className="relative bg-white overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-[#E5E5E5]/50">
        <div className={`max-w-[1200px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center gap-4 md:gap-8 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <a href="/" aria-label="Qykfin Home">
              <QykfinLogoSimple />
            </a>
            <div className={`hidden md:flex items-center gap-5 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <a href="#features" className="text-[13px] text-[#1D1D1F] hover:text-[#0071E3] transition-colors font-inter font-medium">{t.nav.features}</a>
              <a href="#how-it-works" className="text-[13px] text-[#1D1D1F] hover:text-[#0071E3] transition-colors font-inter font-medium">{t.nav.howItWorks}</a>
              <a href="/uae-loan-calculator" className="text-[13px] text-[#1D1D1F] hover:text-[#0071E3] transition-colors font-inter font-medium">{t.nav.calculator}</a>
              <a href="/blog" className="text-[13px] text-[#1D1D1F] hover:text-[#0071E3] transition-colors font-inter font-medium">{t.nav.blog}</a>
              <a href="/about-us" className="text-[13px] text-[#1D1D1F] hover:text-[#0071E3] transition-colors font-inter font-medium">{t.nav.about}</a>
            </div>
          </div>
          <div className={`flex items-center gap-2 sm:gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <LanguageToggle />
            <Button 
              className="hidden sm:flex bg-[#1D1D1F] text-white hover:bg-[#1D1D1F]/90 rounded-full px-5 py-2 text-[13px] font-medium h-9"
              onClick={() => document.getElementById('hero-email')?.focus()}
              data-testid="nav-get-started"
            >
              {t.nav.getStarted}
            </Button>
            <button 
              className="md:hidden p-2 text-[#1D1D1F]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className={`md:hidden bg-white border-t border-[#E5E5E5]/50 px-4 py-4 space-y-1 ${isRTL ? 'text-right' : ''}`}>
            <a href="#features" className="block py-2.5 text-[15px] text-[#1D1D1F] hover:text-[#0071E3] font-inter" onClick={() => setMobileMenuOpen(false)}>{t.nav.features}</a>
            <a href="#how-it-works" className="block py-2.5 text-[15px] text-[#1D1D1F] hover:text-[#0071E3] font-inter" onClick={() => setMobileMenuOpen(false)}>{t.nav.howItWorks}</a>
            <a href="/uae-loan-calculator" className="block py-2.5 text-[15px] text-[#1D1D1F] hover:text-[#0071E3] font-inter" onClick={() => setMobileMenuOpen(false)}>{t.nav.calculator}</a>
            <a href="/blog" className="block py-2.5 text-[15px] text-[#1D1D1F] hover:text-[#0071E3] font-inter" onClick={() => setMobileMenuOpen(false)}>{t.nav.blog}</a>
            <a href="/about-us" className="block py-2.5 text-[15px] text-[#1D1D1F] hover:text-[#0071E3] font-inter" onClick={() => setMobileMenuOpen(false)}>{t.nav.about}</a>
            <Button 
              className="w-full bg-[#1D1D1F] text-white hover:bg-[#1D1D1F]/90 rounded-full py-2.5 text-[15px] font-medium h-11 mt-3"
              onClick={() => {
                setMobileMenuOpen(false);
                document.getElementById('hero-email')?.focus();
              }}
            >
              {t.nav.getStarted}
            </Button>
          </div>
        )}
      </nav>

      {/* Hero Content */}
      <div className="pt-24 pb-16 md:pt-28 md:pb-20">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${isRTL ? 'direction-rtl' : ''}`}>
            
            {/* Left: Content */}
            <div className={`space-y-6 ${isRTL ? 'lg:order-2 text-right' : ''}`}>
              {/* Badge */}
              <div 
                className={`inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#0071E3]/10 to-[#5856D6]/10 rounded-full border border-[#0071E3]/20 opacity-0 animate-fade-in-up ${isRTL ? 'flex-row-reverse' : ''}`}
                style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}
              >
                <Sparkles className="w-3.5 h-3.5 text-[#0071E3] animate-bounce-subtle" />
                <span className="text-[11px] font-semibold text-[#0071E3] uppercase tracking-wide font-inter">
                  {t.hero.badge}
                </span>
              </div>

              {/* Headline */}
              <h1 
                className="font-dm text-4xl md:text-5xl lg:text-[56px] font-semibold tracking-tight text-[#1D1D1F] leading-[1.05] opacity-0 animate-fade-in-up"
                style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}
              >
                {t.hero.title1}<br />
                <span className="bg-gradient-to-r from-[#0071E3] to-[#5856D6] bg-clip-text text-transparent gradient-text-animated">{t.hero.title2}</span>
              </h1>

              {/* Subheadline */}
              <p 
                className="text-lg text-[#6e6e73] font-inter leading-relaxed max-w-xl opacity-0 animate-fade-in-up"
                style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
              >
                {t.hero.subtitle}
              </p>

              {/* CTA Form */}
              <form 
                onSubmit={handleWaitlist} 
                className={`flex flex-col sm:flex-row gap-3 max-w-md opacity-0 animate-fade-in-up ${isRTL ? 'sm:flex-row-reverse' : ''}`}
                style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
              >
                <Input
                  id="hero-email"
                  type="email"
                  placeholder={t.hero.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`h-12 px-5 rounded-full border-[#E5E5E5] bg-[#F5F5F7] text-[#1D1D1F] placeholder:text-[#86868b] font-inter focus:border-[#0071E3] focus:ring-[#0071E3]/20 focus:bg-white text-[15px] transition-all duration-300 ${isRTL ? 'text-right' : ''}`}
                  data-testid="hero-email-input"
                />
                <Button 
                  type="submit"
                  disabled={loading}
                  className={`h-12 px-7 bg-[#1D1D1F] text-white hover:bg-[#1D1D1F]/90 rounded-full font-medium transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2 text-[15px] whitespace-nowrap btn-ripple ${isRTL ? 'flex-row-reverse' : ''}`}
                  data-testid="hero-submit-btn"
                >
                  {loading ? t.hero.joining : t.hero.joinWaitlist}
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                </Button>
              </form>

              {/* Trust Badges */}
              <div 
                className={`flex flex-wrap items-center gap-6 pt-2 opacity-0 animate-fade-in-up ${isRTL ? 'flex-row-reverse justify-end' : ''}`}
                style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}
              >
                {[
                  { icon: Sparkles, label: t.hero.trustBadge1 },
                  { icon: Shield, label: t.hero.trustBadge2 },
                  { icon: Globe, label: t.hero.trustBadge3 }
                ].map((item, index) => (
                  <div key={index} className={`flex items-center gap-2 text-[#6e6e73] ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <item.icon className="w-4 h-4 text-[#0071E3]" />
                    <span className="text-[13px] font-inter font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: AI Dashboard Mockup */}
            <div className={`relative flex justify-center lg:justify-end ${isRTL ? 'lg:order-1 lg:justify-start' : ''}`}>
              <div 
                className="relative w-full max-w-[420px] opacity-0 animate-fade-in-up"
                style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}
              >
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#0071E3]/20 via-[#5856D6]/15 to-[#34C759]/10 blur-3xl rounded-3xl scale-110 opacity-60 animate-pulse"></div>
                
                {/* Dashboard Card */}
                <div className="relative bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-[#E5E5E5]/60 overflow-hidden hover:shadow-[0_30px_80px_rgba(0,0,0,0.12)] transition-shadow duration-500">
                  {/* Header */}
                  <div className={`bg-gradient-to-r from-[#0071E3] to-[#5856D6] px-6 py-4 ${isRTL ? 'text-right' : ''}`}>
                    <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div>
                        <p className="text-white/70 text-[11px] font-inter">{t.hero.appTitle}</p>
                        <h3 className="text-white font-dm font-semibold text-lg">Qykfin AI</h3>
                      </div>
                      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm animate-bounce-subtle">
                        <Sparkles className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className={`p-5 space-y-4 ${isRTL ? 'text-right' : ''}`}>
                    {/* Missed Rewards Alert */}
                    <div className={`bg-[#FF9500]/10 border border-[#FF9500]/20 rounded-2xl p-4 ${isRTL ? 'text-right' : ''}`}>
                      <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="w-9 h-9 bg-[#FF9500] rounded-xl flex items-center justify-center flex-shrink-0">
                          <AlertCircle className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#FF9500] text-[11px] font-semibold uppercase tracking-wide">{t.hero.missedRewards}</p>
                          <p className="text-[#1D1D1F] font-dm font-bold text-2xl mt-0.5">{t.hero.missedAmount}</p>
                          <p className="text-[#6e6e73] text-[12px] font-inter mt-1">{t.hero.suggestion}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Smart Pay Suggestion */}
                    <div className={`bg-[#34C759]/10 border border-[#34C759]/20 rounded-2xl p-4 ${isRTL ? 'text-right' : ''}`}>
                      <div className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="w-9 h-9 bg-[#34C759] rounded-xl flex items-center justify-center flex-shrink-0">
                          <CreditCard className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#34C759] text-[11px] font-semibold uppercase tracking-wide">{t.hero.smartPaySaving}</p>
                          <p className="text-[#1D1D1F] font-dm font-semibold text-base mt-0.5">{t.hero.savingAmount}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Credit Health */}
                    <div className={`bg-[#F5F5F7] rounded-2xl p-4 ${isRTL ? 'text-right' : ''}`}>
                      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <div className="w-9 h-9 bg-[#0071E3] rounded-xl flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <p className="text-[#6e6e73] text-[11px] font-inter">{t.hero.creditScore}</p>
                            <p className="text-[#1D1D1F] font-dm font-semibold">{t.hero.scoreValue}</p>
                          </div>
                        </div>
                        <div className={`${isRTL ? 'text-left' : 'text-right'}`}>
                          <p className="text-[#34C759] text-[12px] font-medium font-inter">{t.hero.utilization}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* AI Indicator */}
                    <div className={`flex items-center justify-center gap-2 pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-2 h-2 bg-[#34C759] rounded-full animate-pulse"></div>
                      <span className="text-[#6e6e73] text-[11px] font-inter">AI actively monitoring your finances</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
